import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges
} from '@angular/core';
import { BundleSelection } from '@df/catalog/product/bundle-selection';
import { IProductConfigurableSelection, IProductDataV3 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { DfDomModule } from '@df/dom/df-dom.module';
import { StickyPanelDirective } from '@df/legacy/ui/sticky/sticky-panel.directive';
import { GtmService } from '@df/module-gtm/gtm.service';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { Nexus } from '@tomandco/nexus-api-types';
import { EGtmAddToCartVia } from 'projects/app-default/src/app/module/gtm/gtm.service.decorator';
import { PaypalPayInThreeMessageComponent } from 'projects/app-default/src/app/module/paypal-pay-in-three/paypal-pay-in-three-message.component';
import { SizebayService } from 'projects/app-default/src/app/module/sizebay/sizebay.service';
import { IfStoreDirective } from 'projects/app-default/src/app/multisite/if-store.directive';
import { IfNotTradeStoreDirective } from 'projects/app-default/src/app/multisite/trade/if-not-trade-store.directive';
import { IfTradeStoreDirective } from 'projects/app-default/src/app/multisite/trade/if-trade-store.directive';
import { TradeClassDirective } from 'projects/app-default/src/app/multisite/trade/trade-class.directive';
import { BreadcrumbsComponent } from 'projects/app-default/src/app/ui/common/breadcrumbs.component';
import { ScrollNavScope } from 'projects/app-default/src/app/ui/scroll-nav/scroll-nav-scope';
import { ScrollNavComponent } from 'projects/app-default/src/app/ui/scroll-nav/scroll-nav.component';
import { BottomPanelComponent } from 'projects/app-default/src/app/ui/widget/bottom-panel/bottom-panel.component';
import { Subscription } from 'rxjs';
import { LocalProduct } from '../../../model/product/product.decorator';
import { ICmsOutletBlockComponent } from '../../../outlet-block/icms-outlet-block.component';
import { ProductBundleOptionsCreateSetComponent } from '../bundle-options/create-set/product-bundle-options-create-set.component';
import { ProductBundleOptionsSelectedComponent } from '../bundle-options/selected/product-bundle-options-selected.component';
import { ProductViewCmsCarouselComponent } from '../cms/carousel/product-view-cms-carousel.component';
import { ProductViewCmsSimpleCarouselBlock } from '../cms/simple-carousel/product-view-cms-simple-carousel.component';
import { ProductConfigurableOptionsComponent } from '../configurable-options/product-configurable-options.component';
import { FeaturesSectionComponent } from '../features-section/features-section.component';
import { NProductPosModal } from '../pos-modal/product-pos-modal.component';
import { ProductGalleryComponent } from '../product-gallery.component';
import { ProductHighlightsComponent } from '../product-highlights/product-highlights.component';
import { ProductViewPriceComponent } from '../product-price/product-view-price.component';
import { ProductPriceTradeComponent } from '../product-price/trade/product-price-trade.component';
import { ProductReviewsSummaryComponent } from '../product-reviews-summary/product-reviews-summary.component';
import { ProductViewAddToBasketComponent } from '../product-view-add-to-basket/product-view-add-to-basket.component';
import { ProductViewDeliveryNoteComponent } from '../product-view-delivery-note/product-view-delivery-note.component';
import { ProductViewDetailsComponent } from '../product-view-details/product-view-details.component';
import { ProductViewFashionRecommendationComponent } from '../product-view-fashion-recommendation/product-view-fashion-recommendation.component';
import { ProductViewFreeGiftBunnnerComponent } from '../product-view-free-gift-bunner/product-view-free-gift-bunner.component';
import { ProductViewKlarnaMsgBlockComponent } from '../product-view-klarna-msg/product-view-klarna-msg.component';
import { ProductViewTitleComponent } from '../product-view-title/product-view-title.component';
import { SpecificationSectionComponent } from '../specification-section/specification-section.component';
import { ProductViewSurchargeNoteComponent } from '../surcharge/note/product-view-surcharge-note.component';
import { NProductViewLayout } from './product-view-layout.interface';
import {
    ProductDeliveryInfoModalComponent,
    productDeliveryInfoModalId
} from '../product-delivery-info-modal/product-delivery-info-modal.component';
import { surviveTreeShaking } from '@df/ng/survive-tree-shaking';
import { InlineButtonComponent } from 'projects/app-default/src/app/ui/buttons/inline-button/inline-button.component';

@Component({
    selector: 'product-view-layout',
    templateUrl: './product-view-layout.html',
    styleUrls: ['./product-view-layout.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        BottomPanelComponent,
        BreadcrumbsComponent,
        DfUiModule,
        DfDomModule,
        FeaturesSectionComponent,
        ICmsClientModule,
        ICmsOutletBlockComponent,
        ICmsOutletBlockComponent,
        IfNotTradeStoreDirective,
        IfTradeStoreDirective,
        IfStoreDirective,
        NgClass,
        NgTemplateOutlet,
        PriceComponent,
        PaypalPayInThreeMessageComponent,
        ProductBundleOptionsCreateSetComponent,
        ProductBundleOptionsSelectedComponent,
        forwardRef(() => ProductConfigurableOptionsComponent),
        ProductGalleryComponent,
        ProductHighlightsComponent,
        ProductPriceTradeComponent,
        ProductReviewsSummaryComponent,
        ProductViewAddToBasketComponent,
        ProductViewCmsCarouselComponent,
        ProductViewCmsSimpleCarouselBlock,
        ProductViewDeliveryNoteComponent,
        ProductViewDetailsComponent,
        ProductViewFashionRecommendationComponent,
        ProductViewFreeGiftBunnnerComponent,
        ProductViewKlarnaMsgBlockComponent,
        ProductViewPriceComponent,
        ProductViewSurchargeNoteComponent,
        ProductViewTitleComponent,
        ScrollNavComponent,
        SizeClassDirective,
        SpecificationSectionComponent,
        StickyPanelDirective,
        TradeClassDirective,
        TranslationModule
    ],
    providers: [{ provide: ScrollNavScope, useClass: ScrollNavScope }]
})
@ICmsComponent<ProductViewLayoutComponent>({
    id: NProductViewLayout.BLOCK_ID,
    block: {
        url: NProductViewLayout.BLOCK_ID
    },
    onDemandOnly: true,
    name: 'Product page layout',
    description: 'Product page layout block',
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-delivery',
            title: 'Delivery and returns'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'deliveryNote',
            title: 'Delivery label copy',
            default: 'Free Delivery on orders over £75'
        },
        {
            type: NICmsContentField.EFieldType.textarea,
            key: 'deliveryCopy',
            title: 'Delivery and returns section content'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-delivery',
            title: 'Fashion recommendation'
        },
        {
            type: NICmsContentField.EFieldType.toggle,
            key: 'hideRetailQty',
            title: 'Hide quantity selector for retail',
            default: false
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'fashionRecHeading',
            title: 'Fashion recommendation heading',
            default: 'Fashion recommendation'
        },
        <NICmsContentField.IComponentsField>{
            key: 'socialComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Social components',
            itemName: 'component',
            default: []
        },
        <NICmsContentField.IComponentsField>{
            key: 'middleComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Middle components',
            itemName: 'component',
            default: []
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-bottom',
            title: 'Bottom part'
        },
        <NICmsContentField.IComponentsField>{
            key: 'bottomComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Bottom components',
            itemName: 'component',
            default: []
        },
        <NICmsContentField.IComponentsField>{
            key: 'instagramFeedComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Instagram feed components',
            itemName: 'component',
            default: []
        },
        // <NICmsContentField.IComponentsField>{
        //     key: 'reviewsComponents',
        //     type: NICmsContentField.EFieldType.components,
        //     title: 'Reviews components',
        //     itemName: 'component',
        //     default: []
        // },
        <NICmsContentField.IComponentsField>{
            key: 'rightComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Right components',
            itemName: 'component',
            default: []
        }
    ]
})
export class ProductViewLayoutComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnChanges, OnDestroy {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NProductViewLayout.IBlockDataContent;

    @Input({
        required: true
    })
    variant!: string;

    @Input({
        required: true
    })
    product!: LocalProduct<IProductDataV3>;

    @Input({
        required: true
    })
    parent!: LocalProduct<IProductDataV3>;

    @Input({
        required: true
    })
    request!: Omit<Nexus.Lib.Basket.BuyRequest, 'id'>;

    @Input()
    bundleSelection?: BundleSelection;

    @Output()
    productChange = new EventEmitter<LocalProduct<IProductDataV3>>();

    @Output()
    matchExactlyChange = new EventEmitter<IProductConfigurableSelection>();

    layout?: NProductViewLayout.ELayout;

    protected productConfigurableSelection?: IProductConfigurableSelection;

    readonly ELayout = NProductViewLayout.ELayout;

    private _bundleSelectionSubscription?: Subscription;

    private previousSelection?: IProductConfigurableSelection;

    private gtmService = inject(GtmService);

    private app = inject(App);

    private dfModalService = inject(DfModalService);

    protected posProductModalStatus = new Status();

    protected posProductModalSuccessStatus = new Status();

    private posProductModalStatusSubscription = new Subscription();

    protected deliveryInfoModalStatus = new Status();

    constructor(
        private afterContentInit: AfterContentInitDirectiveService,
        private renderer2: Renderer2,
        private elementRef: ElementRef
    ) {
        super();
        surviveTreeShaking([ProductDeliveryInfoModalComponent]);
    }

    override ngOnInit() {
        this.afterContentInit.lock(this);
        this.sendSizebayGtmEvent(true);
        this.subscriptions.add(
            this.productChange.subscribe(el => {
                this.sendSizebayGtmEvent();
                this.updateLayout();
            })
        );
        this.updateLayout();
        super.ngOnInit();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        this.matchExactly;
        super.ngOnChanges(changes);
        if (changes['bundleSelection']) {
            this._bundleSelectionSubscription?.unsubscribe();
            const bundleSelection = this.bundleSelection;
            if (bundleSelection) {
                this._bundleSelectionSubscription = bundleSelection.subscribe(() => {
                    this.request.bundle_options = bundleSelection.selection;
                    this.detectChanges();
                });
            }
        }
        this.productChange.emit(this.product);
    }
    ngAfterViewInit(): void {
        this.timeoutService.setTimeout(() => this.toggleReady(true), 200);
        this.timeoutService.setTimeout(() => this.afterContentInit.unlock(this), 1000);
    }

    override ngOnDestroy(): void {
        this._bundleSelectionSubscription?.unsubscribe();
        this.afterContentInit.unlock(this);
        super.ngOnDestroy();
    }

    matchExactly(selection: IProductConfigurableSelection): void {
        this.productConfigurableSelection = Object.assign({}, selection);
        this.matchExactlyChange.emit(selection);
        this.detectChanges();
    }

    protected get hasOneSize(): boolean {
        return !!this.parent?.getConfigurableAttributeOptions('size')?.find(el => el.label === 'One Size');
    }
    /**
     * Video block variant
     */
    get videoBlockVariant(): string {
        return this.parent?.id?.toString() || this.product.id.toString() || 'default';
    }

    get editorialBlockVariant(): string {
        return this.parent?.id?.toString() || this.product.id.toString() || 'default';
    }

    protected updateLayout() {
        this.layout = this.product.isBundle ? NProductViewLayout.ELayout.bundle : NProductViewLayout.ELayout.default;
        this.detectChanges();
    }

    protected get gtmAddToCartVia(): EGtmAddToCartVia {
        return this.layout === this.ELayout.bundle ? EGtmAddToCartVia.bundle : EGtmAddToCartVia.pdp;
    }

    private toggleReady(ready: boolean) {
        if (ready) {
            this.renderer2.addClass(this.elementRef.nativeElement, 'initiated');
        }
    }

    protected openPosProductModal(): void {
        this.dfModalService.open(
            this.posProductModalStatus,
            NProductPosModal.MODAL_ID,
            undefined,
            {
                sku: this.product.data?.sku
            },
            modal => {
                this.posProductModalStatus = modal.componentInstance?.status;
                this.onPosProductModalStatusChange();
            }
        );
    }

    protected openDeliveryInfoModal(): void {
        this.dfModalService.open(this.deliveryInfoModalStatus, productDeliveryInfoModalId);
    }

    private onPosProductModalStatusChange(): void {
        this.posProductModalStatusSubscription.unsubscribe();
        this.posProductModalStatusSubscription = this.posProductModalSuccessStatus.subscribe(() => {
            this.detectChanges();
        });
    }

    private sizebayService = inject(SizebayService);

    private sendSizebayGtmEvent(force: boolean = false) {
        if (force || this.shouldSendSizebayGtmEvent()) {
            const eventData = {
                event: 'product_view_sizebay',
                parent_sku: this.product.parent?.data?.sku,
                variant_sku: this.getVariantSku(),
                size: this.productConfigurableSelection?.['size'] || null,
                permalink: this.app.url.storeUrl + this.product.data?.sku
            } as any;
            this.gtmService.push(eventData);
            this.sizebayService.init();
        }
        this.previousSelection = { ...this.productConfigurableSelection };
    }

    private getVariantSku(): string | null {
        if (this.product.isConfigurable || this.product.isGrouped) {
            return this.product.data?.first_simple_sku ?? null;
        }
        return this.product.data?.sku ?? null;
    }

    private shouldSendSizebayGtmEvent(): boolean {
        if (!this.previousSelection?.['color'] && this.productConfigurableSelection?.['size'] !== this.previousSelection?.['size']) {
            return false;
        }
        return this.previousSelection?.['color'] !== this.productConfigurableSelection?.['color'];
    }
}
